import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScanLine, Send, AtSign, CalendarClock, Upload, PiggyBank } from 'lucide-react';
type PlatformId = 'linkedin' | 'x' | 'instagram' | 'youtube' | 'tiktok';
const PLATFORMS: {
  id: PlatformId;
  label: string;
  slug: string;
}[] = [{
  id: 'linkedin',
  label: 'LinkedIn',
  slug: 'linkedin'
}, {
  id: 'x',
  label: 'X',
  slug: 'x'
}, {
  id: 'instagram',
  label: 'Instagram',
  slug: 'instagram'
}, {
  id: 'youtube',
  label: 'YouTube',
  slug: 'youtube'
}, {
  id: 'tiktok',
  label: 'TikTok',
  slug: 'tiktok'
}];
const PLATFORM_COPY: Record<PlatformId, {
  postIt: string;
  tagGiga: string;
  letItRun: string;
  submit: string;
}> = {
  linkedin: {
    postIt: 'Share it on LinkedIn. Add your take. What did you ask? What happened? Why does it matter?',
    tagGiga: "Tag @askgiga. That's how we find you.",
    letItRun: 'Wait 7 days. Let impressions settle. Reply to comments. Build momentum.',
    submit: 'Send us the post link and an analytics screenshot showing impressions. Pick credits or cash.'
  },
  x: {
    postIt: 'Post it on X. Add your take. What did you ask? What happened? Why does it matter?',
    tagGiga: "Tag @askgiga. That's how we find you.",
    letItRun: 'Wait 7 days. Let views settle. Reply to comments. Build momentum.',
    submit: 'Send us the post link and an analytics screenshot showing views. Pick credits or cash.'
  },
  instagram: {
    postIt: 'Post on Instagram. Reels, carousels, and statics all work. Pick the format that fits your audience.',
    tagGiga: "Tag @askgiga. That's how we find you.",
    letItRun: 'Wait 7 days. Let views settle. Reply to comments. Share to Stories for extra reach.',
    submit: 'Send us the post link and an analytics screenshot showing views. Pick credits or cash.'
  },
  youtube: {
    postIt: 'Upload to YouTube. A walkthrough, screen recording, or demo of Giga working.',
    tagGiga: 'Tag @askgiga, mention Giga in the title or description, and link to askgiga.com.',
    letItRun: "Wait 7 days. Let views settle. Reply to comments. Pin it if it's your best work.",
    submit: 'Send us the video link and a YouTube Studio screenshot showing views. Pick credits or cash.'
  },
  tiktok: {
    postIt: 'Post on TikTok. A screen recording of Giga working with your voiceover, or a reaction to the output.',
    tagGiga: "Tag @askgiga in the caption. That's how we find you.",
    letItRun: 'Wait 7 days. Let views settle. Reply to comments. Stitch or duet replies if they land.',
    submit: 'Send us the video link and an analytics screenshot showing views. Pick credits or cash.'
  }
};
const COMMON_COPY = {
  capture: 'Giga did something in your Slack. Screenshot it. Or screen-record it. Whatever shows the real output.',
  getPaid: 'We review within 48 hours. Credits hit your Giga account immediately. Cash goes through Dub within 5 business days.'
};
export function CreatorHowItWorks() {
  const [platform, setPlatform] = useState<PlatformId>('linkedin');
  const copy = PLATFORM_COPY[platform];
  const steps = [{
    num: '01',
    Icon: ScanLine,
    title: 'Capture the output',
    desc: COMMON_COPY.capture
  }, {
    num: '02',
    Icon: PiggyBank,
    title: 'Get your Dub link',
    desc: "Join the Giga partner program and copy your referral link. Put it in your post's first comment or description every time, credits or cash. That's how we track performance."
  }, {
    num: '03',
    Icon: Send,
    title: 'Post it',
    desc: copy.postIt
  }, {
    num: '04',
    Icon: AtSign,
    title: 'Tag Giga',
    desc: copy.tagGiga
  }, {
    num: '05',
    Icon: CalendarClock,
    title: 'Let it run',
    desc: copy.letItRun
  }, {
    num: '06',
    Icon: Upload,
    title: 'Submit',
    desc: copy.submit
  }];
  return <section id="how-it-works" className="py-20 md:py-28 px-6 md:px-8 max-w-7xl mx-auto" data-id="element-2573" data-mp="1p1x2td3y1p3d">
      
      <div className="text-center mb-12" data-id="element-2574" data-mp="1u10910vnu1cx">
        <h2 className="font-title font-bold text-4xl md:text-6xl tracking-tight text-[#2B211A] mb-4" data-id="element-2575" data-mp="1u45xcdwgsz7l">
          How it works
        </h2>
        <p className="text-neutral-500 text-lg" data-id="element-2576" data-mp="1nmb41opui3mh">
          Screenshot. Grab your Dub link. Post. Tag. Wait. Submit. Get paid.
        </p>
      </div>

      <div className="flex justify-center mb-16" data-id="element-2577" data-mp="21mvpksn5adjo">
        <div role="tablist" aria-label="Select a platform" className="inline-flex items-center gap-1 p-1.5 bg-white rounded-full border border-neutral-200 shadow-sm" data-id="element-2578" data-mp="1wly529enx7f0">
          
          {PLATFORMS.map(p => {
          const isActive = platform === p.id;
          return <button key={p.id} role="tab" aria-selected={isActive} aria-label={p.label} onClick={() => setPlatform(p.id)} className="relative flex items-center justify-center w-16 md:w-24 h-11 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#EA580C]" data-id="element-2579" data-mp="26iui8sfiahpw">
                
                {isActive && <motion.div layoutId="how-it-works-platform-pill" className="absolute inset-0 rounded-full bg-[#2B211A] shadow-md" transition={{
              type: 'spring',
              stiffness: 400,
              damping: 32
            }} data-id="element-2580" data-mp="2pbonuvr8oxhg" />}
                <img src={`https://cdn.simpleicons.org/${p.slug}/${isActive ? 'ffffff' : '9ca3af'}`} alt={p.label} className="relative z-10 w-5 h-5 object-contain" loading="lazy" onError={e => {
              const img = e.currentTarget as HTMLImageElement;
              if (!img.dataset.fallback) {
                img.dataset.fallback = '1';
                img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${p.slug}.svg`;
              } else {
                img.style.visibility = 'hidden';
              }
            }} data-id="element-2581" data-mp="3cxqwtq9m7z8k" />
                
              </button>;
        })}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-5" data-id="element-2582" data-mp="2l4zvx2gzlmcz">
        {steps.map((step, i) => {
        const Icon = step.Icon;
        return <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          delay: i * 0.06,
          duration: 0.5
        }} className="relative rounded-[2rem] p-8 md:p-10 bg-white border border-neutral-200 shadow-sm hover:shadow-md transition-shadow group" data-id="element-2583" data-mp="2b7vaj4uytux8" data-mpi={i}>
              
              <div className="flex items-start justify-between mb-10" data-id="element-2584" data-mp="2b3ferzn6ixyo">
                <div className="w-14 h-14 rounded-2xl bg-[#FCF3EC] flex items-center justify-center group-hover:scale-110 transition-transform duration-300" data-id="element-2585" data-mp="2b1cttu0vvv0t">
                  <Icon className="w-6 h-6 text-[#EA580C]" strokeWidth={2} data-id="element-2586" data-mp="21484xmfnllgb" />
                </div>
                <span className="px-3 py-1 rounded-full bg-neutral-100 text-neutral-500 text-xs font-bold tracking-wider" data-id="element-2587" data-mp="1w6y9nq5l2fkk">
                  {step.num}
                </span>
              </div>
              <div className="relative min-h-[110px]" data-id="element-2588" data-mp="kqqbe0x6pqep">
                <h3 className="font-title font-bold text-2xl mb-3 text-[#2B211A]" data-id="element-2589" data-mp="ktcjc1nzm7xg">
                  {step.title}
                </h3>
                <AnimatePresence mode="wait" data-id="element-2590" data-mp="31cwq47nnfh2c">
                  <motion.p key={`${platform}-${i}`} initial={{
                opacity: 0,
                y: 4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.25
              }} className="text-neutral-500 leading-relaxed text-[15px]" data-id="element-2591" data-mp="31dpmqfsw8q6r">
                    
                    {step.desc}
                  </motion.p>
                </AnimatePresence>
                {step.num === '02' && <button className="mt-6 px-5 py-2.5 bg-[#2B211A] text-white rounded-full text-sm font-bold hover:bg-black transition-colors" data-id="element-2592" data-mp="3u1v894rruj7n">
                    Join the partner program
                  </button>}
              </div>
            </motion.div>;
      })}
      </div>

      {/* Step 07 - Get Paid (Full Width) */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="relative rounded-[2.5rem] p-10 md:p-16 bg-[#FCF3EC] border border-neutral-200 shadow-sm text-center max-w-4xl mx-auto" data-id="element-2593" data-mp="5fgu4i8b6c3b">
        
        <div className="relative z-10 flex flex-col items-center" data-id="element-2594" data-mp="a6p1z9tzpped">
          <div className="flex items-center gap-3 mb-6" data-id="element-2595" data-mp="3ims99eh6xc26">
            <div className="w-12 h-12 rounded-2xl bg-[#EA580C] flex items-center justify-center shadow-lg shadow-[#EA580C]/20" data-id="element-2596" data-mp="3inadaxa3gukn">
              <PiggyBank className="w-5 h-5 text-white" strokeWidth={2.25} data-id="element-2597" data-mp="3dq1t6r5dyggr" />
            </div>
            <span className="px-3 py-1 rounded-full bg-neutral-100 text-neutral-500 text-xs font-bold tracking-wider" data-id="element-2598" data-mp="3siv9ufwe92n0">
              07
            </span>
          </div>
          <h3 className="font-title font-bold text-3xl md:text-4xl mb-4 text-[#2B211A]" data-id="element-2599" data-mp="17teck82rdabu">
            Get paid
          </h3>
          <p className="text-neutral-500 leading-relaxed text-lg max-w-xl" data-id="element-2600" data-mp="12xqipfdbzt00">
            {COMMON_COPY.getPaid}
          </p>
        </div>
      </motion.div>
    </section>;
}